import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Divider,
  Paper,
  Grid,
  Skeleton
} from '@mui/material';
import {
  Backup as BackupIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../services/api';

// eslint-disable-next-line max-lines-per-function
export default function SharedDriveBackupModal({ open, onClose, drive }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (open && drive) {
      fetchBackupStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, drive]);

  const fetchBackupStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.getSharedDriveBackupStatus(drive.id);
      setBackupStatus(response.config);
      setBackupHistory(response.history || []);
    } catch (err) {
      if (err.message?.includes('No backup configuration found')) {
        // No backup configured yet
        setBackupStatus(null);
        setBackupHistory([]);
      } else {
        console.error('Error fetching backup status:', err);
        setError(err.message || 'Failed to load backup status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetupBackup = async (enableScheduled) => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await api.setupSharedDriveBackup(drive.id, drive.name, enableScheduled);
      setSuccessMessage('Backup configuration created successfully');

      // Refresh status
      await fetchBackupStatus();
    } catch (err) {
      console.error('Error setting up backup:', err);
      setError(err.message || 'Failed to setup backup');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleScheduledBackup = async (enabled) => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await api.toggleSharedDriveBackup(drive.id, enabled);
      setSuccessMessage(`Scheduled backup ${enabled ? 'enabled' : 'disabled'}`);

      // Update local state
      setBackupStatus(prev => ({
        ...prev,
        scheduledBackupEnabled: enabled
      }));
    } catch (err) {
      console.error('Error toggling scheduled backup:', err);
      setError(err.message || 'Failed to update backup settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerBackup = async () => {
    try {
      setBackupInProgress(true);
      setError(null);
      setSuccessMessage(null);

      setSuccessMessage('Backup completed successfully');

      // Refresh to show new backup in history
      await fetchBackupStatus();
    } catch (err) {
      console.error('Error triggering backup:', err);
      setError(err.message || 'Failed to trigger backup');
    } finally {
      setBackupInProgress(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'partial':
        return <WarningIcon color="warning" />;
      default:
        return null;
    }
  };

  const renderBackupHistory = () => {
    if (backupHistory.length === 0) {
      return (
        <Alert severity="info">
          No backup history available. Run your first backup to get started.
        </Alert>
      );
    }

    return (
      <List>
        {backupHistory.map((backup, index) => (
          <React.Fragment key={backup.id}>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ component: 'div' }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(backup.status)}
                    <Typography variant="subtitle2">
                      {formatDate(backup.timestamp)}
                    </Typography>
                    <Chip
                      label={backup.triggeredBy === 'manual' ? 'Manual' : 'Scheduled'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="caption" color="text.secondary">
                          Files: {backup.stats?.backedUpFiles || 0} backed up,
                          {' '}{backup.stats?.skippedFiles || 0} skipped
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="caption" color="text.secondary">
                          Size: {formatFileSize(backup.stats?.totalSize || 0)}
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="caption" color="text.secondary">
                          Duration: {formatDuration(backup.stats?.duration || 0)}
                        </Typography>
                      </Grid>
                      {backup.stats?.errors?.length > 0 && (
                        <Grid size={12}>
                          <Typography variant="caption" color="error">
                            {backup.stats.errors.length} errors occurred
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                }
              />
            </ListItem>
            {index < backupHistory.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} />
        </Box>
      );
    }

    if (!backupStatus) {
      // No backup configured yet
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <BackupIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Backup Configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set up automated backups to protect your shared drive data.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<BackupIcon />}
              onClick={() => handleSetupBackup(false)}
              disabled={saving}
            >
              Setup Manual Backup Only
            </Button>
            <Button
              variant="contained"
              startIcon={<ScheduleIcon />}
              onClick={() => handleSetupBackup(true)}
              disabled={saving}
            >
              Setup with Nightly Backups
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        {/* Backup Configuration */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Backup Configuration
          </Typography>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={backupStatus.scheduledBackupEnabled || false}
                  onChange={(e) => handleToggleScheduledBackup(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Enable nightly backups at 10:00 PM"
            />
            {backupStatus.lastBackup && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Last backup: {formatDate(backupStatus.lastBackup.timestamp)}
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={backupInProgress ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              onClick={handleTriggerBackup}
              disabled={backupInProgress || saving}
              fullWidth
            >
              {backupInProgress ? 'Backup in Progress...' : 'Backup Now'}
            </Button>
          </Box>
        </Paper>

        {/* Backup History */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              Backup History
            </Typography>
            <IconButton size="small" onClick={fetchBackupStatus} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
          {renderBackupHistory()}
        </Paper>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BackupIcon />
          <Typography variant="h6">
            Backup Settings - {drive?.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {renderContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}